import { IChannelMember } from '../telegram-bots/channels/interfaces';
import { IUserSafe } from '../user/user.interface';

export interface AbstractDocument {
  _id: string;
  createdAt: string;
  updatedAt: string;
}

export enum TelegramButtonType {
  action = 'action',
  url = 'url',
}

export interface ITelegramActionButton {
  type: TelegramButtonType.action;
  action: string;
  label: string;
}

export interface ITelegramUrlButton {
  type: TelegramButtonType.url;
  url: string;
  label: string;
}

export type TelegramButton = ITelegramActionButton | ITelegramUrlButton;

export enum TelegramMessageType {
  media = 'media',
  voice = 'voice',
  videoNote = 'videoNote',
  service = 'service',
  date = 'date',
  sticker = 'sticker',
}

export enum TelegramMessageDirection {
  outcome = 'outcome',
  income = 'income',
  service = 'service',
}

export enum TelegramMessageReason {
  messenger = 'messenger',
  service = 'service',
  broadcast = 'broadcast',
  flow = 'flow',
  postback = 'postback',
  channel = 'channel',
}

export interface ITelegramMessage extends AbstractDocument {
  text: string | null;
  chatId: number;
  telegramId: number;
  dialog: string;
  buttons: TelegramButton[][];
  media: string[];
  type: TelegramMessageType;
  direction: TelegramMessageDirection;
  reason: TelegramMessageReason;
  seen: boolean;
  sender?: string;
  senderName?: string;
  translation?: string;
  deleted?: boolean;
  replyMessageId?: number | null;
}

export interface ITag extends AbstractDocument {
  owner: string;
  project: string;
  text: string;
}

export enum MediaType {
  photo = 'photo',
  video = 'video',
  videoNote = 'videoNote',
  audio = 'audio',
  voice = 'voice',
}

export interface IMedia extends AbstractDocument {
  type: MediaType;
  url: string;
  owner: string;
  name?: string;
  _id: string;
}

export interface ITelegramMediaMessage
  extends Omit<ITelegramMessage, 'type' | 'media'> {
  type: TelegramMessageType.media;
  text: string;
  media: IMedia[];
}

export interface ITelegramVoiceMessage
  extends Omit<ITelegramMessage, 'type' | 'media'> {
  type: TelegramMessageType.voice;
  text: string;
  media: IMedia[];
}

export interface ITelegramVideoNoteMessage
  extends Omit<ITelegramMessage, 'type' | 'media'> {
  type: TelegramMessageType.videoNote;
  media: IMedia[];
}

export interface ITelegramServiceMessage
  extends Omit<
    ITelegramMessage,
    | 'type'
    | 'media'
    | 'buttons'
    | 'telegramId'
    | 'sender'
    | 'chatId'
    | 'direction'
  > {
  text: string;
  type: TelegramMessageType.service;
}

export interface ITelegramDateMessage
  extends Omit<ITelegramServiceMessage, 'type'> {
  text: string;
  type: TelegramMessageType.date;
}

export type ITelegramDisplayedMessage = (
  | ITelegramMessage
  | ITelegramMediaMessage
  | ITelegramDateMessage
  | ITelegramVoiceMessage
  | ITelegramVideoNoteMessage
  | ITelegramServiceMessage
) & { replyText?: string } & { replyMedia?: string };

export type SendTelegramMessage = Omit<
  ITelegramMessage,
  '_id' | 'createdAt' | 'updatedAt' | 'telegramId' | 'mediaGroupId'
>;

export type SendEditTelegramMessage = Pick<
  ITelegramMessage,
  '_id' | 'text' | 'buttons' | 'media'
>;

export type EditTelegramMessage = Pick<
  ITelegramMessage,
  '_id' | 'text' | 'buttons'
> & { media: IMedia[] } & { type?: TelegramMessageType };

export enum TelegramLeadStatus {
  lead = 'LEAD',
  reg = 'REG',
  dep = 'DEP',
  qual = 'QUAL',
}

export type Params = Record<string, string>;

export interface ITelegramBotDialog extends AbstractDocument {
  telegramId: number;
  firstName: string;
  lastName: string;
  username: string;
  avatarUrl: string;
  banned: boolean;
  started: boolean;
  botId: string;
  project: string;
  owner: string | null;
  currentManager: string | null;
  notSeenMessagesCount: number;
  notSeenSince: Date | null;
  description: string;
  blockedByUser: boolean;
  tags: string[];
  channelMemberships: string[];
  params: Params;
  status: string;
  sentFirstManagerMessage: boolean;
  sentFirstManagerMessageWithLink: boolean;
  receivedFirstMessage: boolean;
}

export interface ITelegramBotDialogExtended
  extends Omit<
    ITelegramBotDialog,
    'channelMemberships' | 'tags' | 'currentManager'
  > {
  tags: ITag[];
  channelMemberships: IChannelMember[];
  currentManager: IUserSafe | null;
}

export type Pagination = {
  limit?: number;
  skip?: number;
};
